// GH #1723. One persistent control on both scan surfaces.
//
// The eye and the compare-arrows on the Scan Dashboard used to open two dead ends:
// neither surface could reach the other, and neither offered a way back to the list
// except the browser's back button. This renders on BOTH regardless of state, and
// carries need_id + batch_number across the switch so the scan context survives.
import {
  Box,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  CompareArrows as CompareIcon,
  InfoOutlined as InfoIcon,
  Visibility as MatchesIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'

export type ScanSurface = 'matches' | 'compare'

interface Props {
  needId: number | null
  batchNumber: number | null
  current: ScanSurface
  /** Opens the funding-need detail. Omit and the scan label stays plain text. */
  onShowNeed?: () => void
  /** Where the scan is identified, e.g. "Awatoru Stream Care — batch 4994". */
  scanLabel?: string
  /** Shown as the Matches surface. On the list this closes the dialog; on the
   *  compare page it opens the same dialog in place. */
  onShowMatches: () => void
  /** Leaving for the list. The dialog closes itself first. */
  onShowList?: () => void
}

export default function ScanContextNav({
  needId,
  batchNumber,
  current,
  onShowNeed,
  scanLabel,
  onShowMatches,
  onShowList,
}: Props) {
  const hasScan = needId !== null && batchNumber !== null
  const compareTo = hasScan
    ? `/scans/compare?need_id=${needId}&batch_number=${batchNumber}`
    : '/scans/compare'

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      flexWrap="wrap"
      sx={{
        // Sticks while the grid scrolls: on a 185-row comparison the way back and
        // the surface switch were only reachable by scrolling to the top.
        position: 'sticky',
        // Below the layout's own sticky header, not under it. At top: 0 the bar
        // parked behind the app bar and was invisible for the whole scroll.
        // These are MUI's default toolbar heights; theme.mixins.toolbar.minHeight
        // is only the 56px base and left the bar 8px under the header at sm+.
        top: { xs: '56px', sm: '64px' },
        zIndex: (theme) => theme.zIndex.appBar - 1,
        // The rows scroll underneath, so it needs its own ground and an edge.
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        py: 1,
        mb: 1,
      }}
    >
      <Breadcrumbs aria-label="scan navigation">
        <Link
          component={RouterLink}
          to="/scans"
          onClick={onShowList}
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <BackIcon fontSize="small" />
          All scans
        </Link>
        {scanLabel ? (
          onShowNeed ? (
            <Link
              component="button"
              type="button"
              onClick={onShowNeed}
              underline="hover"
              variant="body2"
              color="text.primary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {scanLabel}
              <InfoIcon fontSize="small" />
            </Link>
          ) : (
            <Typography color="text.primary" variant="body2">
              {scanLabel}
            </Typography>
          )
        ) : null}
      </Breadcrumbs>

      <ToggleButtonGroup size="small" exclusive value={current}>
        <ToggleButton value="matches" onClick={onShowMatches}>
          <MatchesIcon fontSize="small" sx={{ mr: 0.5 }} />
          Matches
        </ToggleButton>
        <ToggleButton
          value="compare"
          component={RouterLink}
          to={compareTo}
          // Disabled rather than hidden: a control that vanishes in some states is
          // what made these two pages feel like dead ends.
          disabled={!hasScan && current === 'compare'}
        >
          <CompareIcon fontSize="small" sx={{ mr: 0.5 }} />
          Engine A/B
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}
